/**
 * Pure helpers for sidebar task rendering.
 *
 * The /task/{user_id} endpoint today returns only {id, status, title}.
 * These helpers are written to DEGRADE GRACEFULLY: they accept any extra
 * fields the backend happens to carry (template_id, trigger_id,
 * created_at, input_data) and use them when present, but produce
 * sensible defaults when they're absent. That way this file can ship
 * ahead of the backend expansion without breaking the UI.
 */

import { parseUtcTimestamp } from "../../utils/dateTime.js";

// ── Source ───────────────────────────────────────────────────────────
//
// Derive the "what kind of run was this?" bucket from whatever fields
// the task carries. Priority: trigger_id > template_id > input_data
// heuristics > fallback to ad-hoc task. Null / undefined fields are
// treated as "field not populated", not "explicitly task".

export const SOURCE = Object.freeze({
  TASK: "task",
  TEMPLATE: "template",
  TRIGGER: "trigger",
  RELIC: "relic",
});

export function sourceOf(task) {
  if (!task || typeof task !== "object") return SOURCE.TASK;

  // Direct FK columns on the Task model — set only when the task was
  // created from that pathway. Covered by the backend-PR expansion.
  if (task.trigger_id) return SOURCE.TRIGGER;
  if (task.template_id) return SOURCE.TEMPLATE;

  // The recent-tasks stream labels the source in a ``type`` (or
  // ``source``) string — "new task" / "template run" / "trigger" — so
  // the sidebar subtitle can read it directly. KEYWORD-match rather than
  // exact-match: the wording varies ("template" vs "template run"), and
  // an exact check left every row falling through to "New task".
  //
  // ``source`` is read before ``type`` because normalizeRecentTasks
  // carries a wrapper-level source hint onto ``source``, leaving ``type``
  // free to also carry the RUN type ("Original_run") — which contains
  // none of these keywords, so it correctly classifies as a plain task.
  const label = String(task.source || task.type || "").toLowerCase();
  if (label.includes("trigger")) return SOURCE.TRIGGER;
  if (label.includes("template")) return SOURCE.TEMPLATE;
  if (label.includes("relic")) return SOURCE.RELIC;
  if (label.includes("task")) return SOURCE.TASK;

  // Fallbacks for cases where the backend hasn't surfaced the FKs or a
  // type label, but the client-side knows the intent (e.g. the init
  // payload).
  const intent = task?.input_data?.intent;
  if (intent === "run_template") return SOURCE.TEMPLATE;
  if (intent === "run_relic") return SOURCE.RELIC;
  if (intent === "trigger_run") return SOURCE.TRIGGER;

  return SOURCE.TASK;
}

// Human label for a task's source bucket — used on the delete modal's
// "original run" chip so the user sees at a glance how the task was
// created (ad-hoc, a template run, a trigger firing, a relic replay).
const SOURCE_LABEL = Object.freeze({
  [SOURCE.TASK]: "New task",
  [SOURCE.TEMPLATE]: "Template",
  [SOURCE.TRIGGER]: "Trigger",
  [SOURCE.RELIC]: "Relic",
});

export function sourceLabelOf(task) {
  return SOURCE_LABEL[sourceOf(task)] || SOURCE_LABEL[SOURCE.TASK];
}

// Title-cased "…Task" noun for the same buckets — used as the delete
// modal's heading ("Delete New Task" / "Delete Template Task" / …) so the
// header itself names what kind of task is being removed.
const SOURCE_NOUN = Object.freeze({
  [SOURCE.TASK]: "Task",
  [SOURCE.TEMPLATE]: "Template Task",
  [SOURCE.TRIGGER]: "Trigger Task",
  [SOURCE.RELIC]: "Relic Task",
});

export function sourceNounOf(task) {
  return SOURCE_NOUN[sourceOf(task)] || SOURCE_NOUN[SOURCE.TASK];
}

// ── Title resolution ────────────────────────────────────────────────
//
// The backend's `title` column is usually the first user message,
// truncated. When the agent later emits a task_summary with a proper
// `task_name`, the UI caches that in localStorage keyed by task id so
// subsequent sidebar renders show the friendly name even after the
// session ends. Same cache is populated live by the Workspace onMessage
// handler via `rememberTaskName()` below.

const TITLE_CACHE_KEY = "hiq.sidebar.taskNames.v1";

function readCache() {
  try {
    const raw = localStorage.getItem(TITLE_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeCache(cache) {
  try {
    localStorage.setItem(TITLE_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* quota / privacy mode — silently skip */
  }
}

export function rememberTaskName(taskId, taskName) {
  const id = taskId ? String(taskId) : "";
  const name = typeof taskName === "string" ? taskName.trim() : "";
  if (!id || !name) return;
  const cache = readCache();
  if (cache[id] === name) return;
  cache[id] = name;
  writeCache(cache);
}

export function forgetTaskName(taskId) {
  const id = taskId ? String(taskId) : "";
  if (!id) return;
  const cache = readCache();
  if (cache[id] === undefined) return;
  delete cache[id];
  writeCache(cache);
}

export function titleOf(task) {
  if (!task || typeof task !== "object") return "";
  const id = String(task.task_id ?? task.id ?? "");
  if (id) {
    const cached = readCache()[id];
    if (cached) return cached;
  }
  // Runtime fallbacks: the backend stores `title`; older data may have
  // used `taskTitle`; ultimately fall back to an empty string so the
  // renderer can decide what placeholder to show.
  return task.title ?? task.taskTitle ?? "";
}

// ── Date bucketing ──────────────────────────────────────────────────
//
// Group tasks by a small set of human-meaningful windows so the list
// reads top-down chronologically without a sea of timestamps.
//
// Buckets (in display order):
//   Today                 — same calendar day
//   Yesterday             — previous calendar day
//   Earlier this week     — same ISO week, not Today/Yesterday
//   Last week             — previous ISO week
//   Earlier this month    — same month, not covered above
//   Older                 — everything else (including undated)
//
// Tasks without a `created_at` land in "Older" so we still render them
// in the chronological-by-position order the backend sent.

const BUCKETS = [
  "Today",
  "Yesterday",
  "Earlier this week",
  "Last week",
  "Earlier this month",
  "Older",
];

function startOfDay(d) {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function daysDiff(a, b) {
  return Math.floor((startOfDay(a) - startOfDay(b)) / 86_400_000);
}

function startOfIsoWeek(d) {
  // Monday-start ISO week. getDay() returns 0 (Sun) … 6 (Sat);
  // we need to roll back to the most recent Monday.
  const copy = startOfDay(d);
  const weekday = copy.getDay(); // 0..6
  const offset = weekday === 0 ? 6 : weekday - 1;
  copy.setDate(copy.getDate() - offset);
  return copy;
}

function sameCalendarMonth(date, ref) {
  return (
    date.getFullYear() === ref.getFullYear() &&
    date.getMonth() === ref.getMonth()
  );
}

export function bucketFor(task, now = new Date()) {
  const raw = task?.created_at;
  if (!raw) return "Older";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "Older";

  const delta = daysDiff(now, d);
  if (delta === 0) return "Today";
  if (delta === 1) return "Yesterday";

  // ISO-week windows: "this week" = current Monday → today,
  // "last week" = previous Monday → previous Sunday. Handles the
  // Wed-viewing-last-Friday case correctly (the 7-day rolling window
  // would mis-bucket that as "Earlier this week").
  const thisWeekStart = startOfIsoWeek(now);
  const lastWeekStart = new Date(thisWeekStart);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);

  const dStart = startOfDay(d);
  if (dStart >= thisWeekStart) return "Earlier this week";
  if (dStart >= lastWeekStart) return "Last week";

  if (sameCalendarMonth(d, now)) return "Earlier this month";
  return "Older";
}

export function groupByBucket(tasks, now = new Date()) {
  const groups = new Map(BUCKETS.map((b) => [b, []]));
  for (const t of Array.isArray(tasks) ? tasks : []) {
    const bucket = bucketFor(t, now);
    groups.get(bucket).push(t);
  }
  // Preserve the BUCKETS display order; drop empty buckets so the list
  // doesn't show section headers with zero children.
  return BUCKETS.map((label) => ({
    label,
    tasks: groups.get(label),
  })).filter((g) => g.tasks.length > 0);
}

// ── Timestamp formatting ────────────────────────────────────────────
//
// Row-level time label. Buckets already group rows by day (Today /
// Yesterday / …), so the per-row label only needs the wall-clock time.

export function timeLabel(task) {
  const raw = task?.created_at;
  if (!raw) return "";
  // Backend often returns bare "YYYY-MM-DD HH:MM:SS" without a timezone
  // marker; `new Date()` would interpret that as local time and produce
  // a label hours off from reality. parseUtcTimestamp anchors it to UTC
  // so the toLocale* call below converts to the user's wall clock.
  const d = parseUtcTimestamp(raw);
  if (!d) return "";

  return d
    .toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toUpperCase();
}

// Absolute calendar date for a task (e.g. "21 May 2026"), derived from
// `created_at`. Empty string when the field is missing / unparseable.
// Used alongside timeLabel in the recents hover tooltip.
export function dateLabel(task) {
  const raw = task?.created_at;
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// ── Status bucket ───────────────────────────────────────────────────

export function statusKind(task) {
  const s = (task?.status ?? "").toLowerCase();
  if (!s) return "idle";
  if (s === "running" || s === "in_progress" || s === "in-progress")
    return "running";
  if (s === "interrupt" || s === "interrupted" || s === "paused")
    return "interrupt";
  if (s === "failed" || s === "error" || s === "errored") return "failed";
  if (s === "cancelled" || s === "canceled") return "cancelled";
  if (
    s === "finished" ||
    s === "completed" ||
    s === "complete" ||
    s === "done"
  ) {
    return "done";
  }
  return "idle";
}

// The subtask / draft runs carried on a recent task. Tolerant of the
// field name so a backend rename degrades to "no drafts" rather than
// throwing.
export function subtasksOf(task) {
  if (!task || typeof task !== "object") return [];
  for (const key of ["subtasks", "drafts", "runs", "children", "versions"]) {
    if (Array.isArray(task[key])) return task[key];
  }
  return [];
}

/** The row's own id, as a string. Empty when the task carries none. */
function idOf(task) {
  if (!task || typeof task !== "object") return "";
  return String(task.task_id ?? task.id ?? "");
}

/**
 * Every task id one sidebar row stands for — its own, plus every
 * subtask (draft) id beneath it, at any depth.
 *
 * A sidebar row is the whole run TREE, not just the original run: the
 * RUNS box navigates to `/task/{subtaskId}` when the user picks a draft,
 * and an Edit & re-run branches yet another id off the same tree. Those
 * ids never appear as rows of their own, so matching on the parent id
 * alone left the list with nothing highlighted the moment a draft was
 * opened.
 *
 * @param {object} task
 * @returns {string[]} ids, parent first; `[]` on junk input.
 */
export function runIdsOf(task) {
  const ids = [];
  const seenIds = new Set();
  // Cycle guard keyed on node IDENTITY, not id: a backend that echoes a
  // node back as its own descendant would otherwise queue it forever.
  // Identity also covers a cycle among id-less nodes, which an id-keyed
  // guard can't see.
  const walked = new Set();
  const queue = [task];
  while (queue.length) {
    const node = queue.shift();
    if (!node || typeof node !== "object" || walked.has(node)) continue;
    walked.add(node);
    const id = idOf(node);
    if (id && !seenIds.has(id)) {
      seenIds.add(id);
      ids.push(id);
    }
    queue.push(...subtasksOf(node));
  }
  return ids;
}

/**
 * Whether a sidebar row should read as ACTIVE for the task id currently
 * open in the URL — true for the row's own id and for any of its drafts,
 * so selecting a run in the pinned panel keeps the parent row lit.
 */
export function ownsTaskId(task, openId) {
  const target = openId == null ? "" : String(openId);
  if (!target) return false;
  return runIdsOf(task).includes(target);
}

// Display the backend status as-is, just sentence-cased so a mixed-case
// feed reads consistently ("complete" → "Complete"); an all-caps value
// like "UPLOAD" is left alone. Used as the fallback for a status we
// can't classify — better to show the backend's own word than to
// mislabel it.
function humanizeStatus(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  return s === s.toUpperCase() ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

// Canonical run-status wording, shared with the RUNS box in the pinned
// panel so the same run reads identically in both places:
//   running → Active · completed/finished → Done
//   interrupt → Action Required · failed → Failed · cancelled → Cancelled
const RUN_STATUS_LABEL = {
  running: "Active",
  done: "Done",
  interrupt: "Action Required",
  failed: "Failed",
  cancelled: "Cancelled",
};

/**
 * The label shown next to a run in the hover tooltip. Canonical wording
 * for every status we recognise; anything else falls back to the
 * backend's own text so an unmapped state stays visible rather than
 * being renamed into something wrong.
 */
export function runStatusLabel(raw) {
  return RUN_STATUS_LABEL[statusKind({ status: raw })] || humanizeStatus(raw);
}

// Turn the backend run `type` into a readable label:
//   "Original_run" → "Original run", "Draft1" → "Draft 1".
// Underscores become spaces, a digit run gets a leading space, and the
// first letter is capitalised. ``fallback`` covers a missing type.
function humanizeRunType(raw, fallback) {
  const s = String(raw ?? "").trim();
  if (!s) return fallback;
  return s
    .replace(/_/g, " ")
    .replace(/([a-zA-Z])(\d)/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
}

/**
 * Run rows for a recent task's hover tooltip (see the sidebar design):
 * the task itself as the "Original run", then each subtask as its draft.
 * The row label uses the backend `type` ("Original_run" → "Original run",
 * "Draft1" → "Draft 1") so the real draft numbers show; each row also
 * carries the status kind (for the dot / text colour) and the canonical
 * status label ("Done", "Active", "Action Required", …).
 */
// How many runs a task can hold (original + its drafts) — mirrors the
// RUNS box's slot count in the pinned panel.
export const RUN_SLOTS = 5;

/**
 * Status kinds for the run-dot meter on a sidebar row: one dot per run
 * (original + each draft), then ``"empty"`` placeholders for the unused
 * slots — e.g. ``["done", "interrupt", "interrupt", "empty", "empty"]``.
 *
 * The meter shows on "New task" AND template rows, from the FIRST run
 * (a single-run task reads one filled dot + four empty). Templates now
 * branch draft versions (result-card Edit & re-run), so their meter
 * matches a new task's. Trigger / relic runs return ``[]`` — they don't
 * branch drafts, so a meter there would be noise; those rows fall back to
 * the status word instead.
 */
export function runDotsOf(task, totalSlots = RUN_SLOTS) {
  const src = sourceOf(task);
  if (src !== SOURCE.TASK && src !== SOURCE.TEMPLATE) return [];
  const rows = runRowsOf(task);
  if (rows.length === 0) return [];
  const kinds = rows.map((r) => r.kind).slice(0, totalSlots);
  while (kinds.length < totalSlots) kinds.push("empty");
  return kinds;
}

// A run's own display name — its saved title, if any. The original run
// borrows the task title; a draft carries its own (``title`` / ``name`` /
// ``task_name`` are all tolerated so a backend rename degrades to "no
// name" rather than throwing). Empty string when nothing usable is set —
// callers fall back to the run's kind label.
function runNameOf(node) {
  if (!node || typeof node !== "object") return "";
  for (const key of ["title", "name", "task_name"]) {
    const v = node[key];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

export function runRowsOf(task) {
  if (!task || typeof task !== "object") return [];
  const rows = [
    {
      // The parent is ALWAYS the original run, so its label is fixed —
      // NOT read from ``task.type``, which the recent-tasks stream now
      // uses for the SOURCE ("template run" / "new task" / "trigger").
      // Reading it here would mislabel the tooltip's first run row.
      label: "Original run",
      name: titleOf(task),
      kind: statusKind(task),
      // Raw backend status kept ALONGSIDE the bucketed ``kind`` so callers
      // can key on the exact value ("finished") — ``kind`` folds
      // finished/complete/done into one "done", which is too coarse for
      // the "Template saved" chip.
      status: task.status,
      statusLabel: runStatusLabel(task.status),
    },
  ];
  subtasksOf(task).forEach((sub, i) => {
    rows.push({
      label: humanizeRunType(sub?.type, `Draft ${i + 1}`),
      name: runNameOf(sub),
      kind: statusKind(sub),
      status: sub?.status,
      statusLabel: runStatusLabel(sub?.status),
    });
  });
  return rows;
}
